import * as THREE from 'three'
import { Heightmap } from '../world/heightmap'

/**
 * Ray marching preko visinske mape. Brže i stabilnije od raycasta
 * nad mrežom od pola miliona trouglova, a za mapu je dovoljno tačno.
 */
export function pickTerrain(
  hm: Heightmap,
  origin: THREE.Vector3,
  dir: THREE.Vector3,
  maxT: number,
  out: THREE.Vector3,
): boolean {
  const korak = hm.cell * 1.5
  let t = 0
  let prevT = 0
  let prevRazlika = origin.y - Math.max(0, hm.sample(origin.x, origin.z))
  if (prevRazlika < 0) {
    out.copy(origin)
    return true
  }
  while (t < maxT) {
    t += korak * (1 + t / (hm.size * 0.5))
    const x = origin.x + dir.x * t
    const y = origin.y + dir.y * t
    const z = origin.z + dir.z * t
    const razlika = y - Math.max(0, hm.sample(x, z))
    if (razlika <= 0) {
      // Bisekcija između poslednja dva uzorka.
      let lo = prevT
      let hi = t
      for (let i = 0; i < 24; i++) {
        const mid = (lo + hi) * 0.5
        const mx = origin.x + dir.x * mid
        const my = origin.y + dir.y * mid
        const mz = origin.z + dir.z * mid
        if (my - Math.max(0, hm.sample(mx, mz)) > 0) lo = mid
        else hi = mid
      }
      out.set(origin.x + dir.x * hi, origin.y + dir.y * hi, origin.z + dir.z * hi)
      return true
    }
    prevT = t
    prevRazlika = razlika
  }
  void prevRazlika
  return false
}

const MIN_PITCH = THREE.MathUtils.degToRad(12)
const MAX_PITCH = THREE.MathUtils.degToRad(84)

/**
 * Kamera u maniru strateške igre: prevlačenje pomera mapu,
 * desni taster rotira, točkić zumira ka kursoru.
 */
export class RtsControls {
  target = new THREE.Vector3()
  private zeljeniTarget = new THREE.Vector3()
  private yaw: number
  private zeljeniYaw: number
  private pitch = THREE.MathUtils.degToRad(46)
  private zeljeniPitch = THREE.MathUtils.degToRad(46)
  private distanca: number
  private zeljenaDistanca: number

  private minDistanca: number
  private maxDistanca: number
  private granica: number

  private pokazivaci = new Map<number, { x: number; y: number }>()
  private rezim: 'nema' | 'pomeranje' | 'rotacija' = 'nema'
  private tasteri = new Set<string>()
  private prosloRastojanje = 0
  private prosliUgao = 0

  private raycaster = new THREE.Raycaster()
  private pom = new THREE.Vector3()
  private pom2 = new THREE.Vector3()
  private ravan = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0)

  constructor(
    private camera: THREE.PerspectiveCamera,
    private dom: HTMLElement,
    private hm: Heightmap,
  ) {
    this.yaw = Math.PI * 0.25
    this.zeljeniYaw = this.yaw
    this.distanca = hm.size * 0.75
    this.zeljenaDistanca = this.distanca
    this.minDistanca = hm.size * 0.035
    this.maxDistanca = hm.size * 1.5
    this.granica = hm.size * 0.55

    dom.addEventListener('pointerdown', this.naPointerDown)
    dom.addEventListener('pointermove', this.naPointerMove)
    dom.addEventListener('pointerup', this.naPointerUp)
    dom.addEventListener('pointercancel', this.naPointerUp)
    dom.addEventListener('wheel', this.naWheel, { passive: false })
    dom.addEventListener('contextmenu', (e) => e.preventDefault())
    window.addEventListener('keydown', this.naKeyDown)
    window.addEventListener('keyup', this.naKeyUp)
    window.addEventListener('blur', this.naBlur)
  }

  dispose(): void {
    this.dom.removeEventListener('pointerdown', this.naPointerDown)
    this.dom.removeEventListener('pointermove', this.naPointerMove)
    this.dom.removeEventListener('pointerup', this.naPointerUp)
    this.dom.removeEventListener('pointercancel', this.naPointerUp)
    this.dom.removeEventListener('wheel', this.naWheel)
    window.removeEventListener('keydown', this.naKeyDown)
    window.removeEventListener('keyup', this.naKeyUp)
    window.removeEventListener('blur', this.naBlur)
  }

  /** Postavlja kameru na početni pogled nad mapom. */
  reset(): void {
    this.zeljeniTarget.set(0, 0, 0)
    this.zeljeniYaw = Math.PI * 0.25
    this.zeljeniPitch = THREE.MathUtils.degToRad(46)
    this.zeljenaDistanca = this.hm.size * 0.75
  }

  /** Usmeravanje kamere spolja — npr. "prikaži Beograd". */
  postavi(opts: { x?: number; z?: number; distanca?: number; yaw?: number; pitch?: number }): void {
    if (opts.x !== undefined) this.zeljeniTarget.x = opts.x
    if (opts.z !== undefined) this.zeljeniTarget.z = opts.z
    if (opts.distanca !== undefined) {
      this.zeljenaDistanca = THREE.MathUtils.clamp(opts.distanca, this.minDistanca, this.maxDistanca)
    }
    if (opts.yaw !== undefined) this.zeljeniYaw = opts.yaw
    if (opts.pitch !== undefined) {
      this.zeljeniPitch = THREE.MathUtils.clamp(opts.pitch, MIN_PITCH, MAX_PITCH)
    }
  }

  /** Trenutna udaljenost kamere — koristi UI za prikaz. */
  get udaljenost(): number {
    return this.distanca
  }

  get azimut(): number {
    return this.yaw
  }

  private naBlur = (): void => {
    this.tasteri.clear()
    this.pokazivaci.clear()
    this.rezim = 'nema'
    this.dom.classList.remove('dragging', 'rotating')
  }

  private naPointerDown = (e: PointerEvent): void => {
    this.dom.setPointerCapture(e.pointerId)
    this.pokazivaci.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (this.pokazivaci.size === 1) {
      this.rezim = e.button === 2 || e.button === 1 || e.shiftKey ? 'rotacija' : 'pomeranje'
      this.dom.classList.add(this.rezim === 'rotacija' ? 'rotating' : 'dragging')
    } else if (this.pokazivaci.size === 2) {
      this.rezim = 'nema'
      this.dom.classList.remove('dragging', 'rotating')
      const [a, b] = [...this.pokazivaci.values()]
      this.prosloRastojanje = Math.hypot(a.x - b.x, a.y - b.y)
      this.prosliUgao = Math.atan2(b.y - a.y, b.x - a.x)
    }
  }

  private naPointerMove = (e: PointerEvent): void => {
    const stari = this.pokazivaci.get(e.pointerId)
    if (!stari) return
    const dx = e.clientX - stari.x
    const dy = e.clientY - stari.y
    stari.x = e.clientX
    stari.y = e.clientY

    if (this.pokazivaci.size === 2) {
      const [a, b] = [...this.pokazivaci.values()]
      const rastojanje = Math.hypot(a.x - b.x, a.y - b.y)
      const ugao = Math.atan2(b.y - a.y, b.x - a.x)
      if (this.prosloRastojanje > 0) {
        this.zoom(-(rastojanje - this.prosloRastojanje) * 0.012)
        this.zeljeniYaw -= ugao - this.prosliUgao
      }
      this.prosloRastojanje = rastojanje
      this.prosliUgao = ugao
      return
    }

    if (this.rezim === 'rotacija') {
      this.zeljeniYaw -= dx * 0.005
      this.zeljeniPitch = THREE.MathUtils.clamp(this.zeljeniPitch - dy * 0.004, MIN_PITCH, MAX_PITCH)
    } else if (this.rezim === 'pomeranje') {
      // Skala je vezana za visinu kamere, pa prevlačenje "drži" teren pod kursorom.
      const skala = (this.distanca * 0.0016) / Math.max(0.35, Math.sin(this.pitch))
      this.pomeriRavno(-dx * skala, -dy * skala)
    }
  }

  private naPointerUp = (e: PointerEvent): void => {
    this.pokazivaci.delete(e.pointerId)
    if (this.pokazivaci.size < 2) this.prosloRastojanje = 0
    if (this.pokazivaci.size === 0) {
      this.rezim = 'nema'
      this.dom.classList.remove('dragging', 'rotating')
    }
  }

  private naWheel = (e: WheelEvent): void => {
    e.preventDefault()
    const kolicina = THREE.MathUtils.clamp(e.deltaY, -120, 120) * 0.0016
    // Zum ka kursoru: cilj se blago privlači tački pod mišem.
    if (kolicina < 0 && this.tackaPodMisem(e, this.pom2)) {
      const faktor = Math.min(0.55, -kolicina * 1.6)
      this.zeljeniTarget.x += (this.pom2.x - this.zeljeniTarget.x) * faktor
      this.zeljeniTarget.z += (this.pom2.z - this.zeljeniTarget.z) * faktor
    }
    this.zoom(kolicina)
  }

  private zoom(kolicina: number): void {
    this.zeljenaDistanca = THREE.MathUtils.clamp(
      this.zeljenaDistanca * Math.exp(kolicina),
      this.minDistanca,
      this.maxDistanca,
    )
  }

  private naKeyDown = (e: KeyboardEvent): void => {
    if (e.target instanceof HTMLInputElement) return
    this.tasteri.add(e.code)
  }

  private naKeyUp = (e: KeyboardEvent): void => {
    this.tasteri.delete(e.code)
  }

  /** Pomeraj u ravni mape, u lokalnom sistemu kamere. */
  private pomeriRavno(desno: number, napred: number): void {
    const sin = Math.sin(this.yaw)
    const cos = Math.cos(this.yaw)
    this.zeljeniTarget.x += desno * cos - napred * sin
    this.zeljeniTarget.z += desno * sin + napred * cos
  }

  /** Tačka terena ispod kursora, ili presek sa ravni mora. */
  tackaPodMisem(e: { clientX: number; clientY: number }, out: THREE.Vector3): boolean {
    const r = this.dom.getBoundingClientRect()
    const ndc = new THREE.Vector2(
      ((e.clientX - r.left) / r.width) * 2 - 1,
      -((e.clientY - r.top) / r.height) * 2 + 1,
    )
    this.raycaster.setFromCamera(ndc, this.camera)
    const o = this.raycaster.ray.origin
    const d = this.raycaster.ray.direction
    if (pickTerrain(this.hm, o, d, this.hm.size * 4, out)) return true
    return this.raycaster.ray.intersectPlane(this.ravan, out) !== null
  }

  update(dt: number): void {
    const brzo = this.tasteri.has('ShiftLeft') || this.tasteri.has('ShiftRight') ? 2.6 : 1
    const korak = this.distanca * 0.9 * dt * brzo
    let dx = 0
    let dz = 0
    if (this.tasteri.has('KeyW') || this.tasteri.has('ArrowUp')) dz -= korak
    if (this.tasteri.has('KeyS') || this.tasteri.has('ArrowDown')) dz += korak
    if (this.tasteri.has('KeyA') || this.tasteri.has('ArrowLeft')) dx -= korak
    if (this.tasteri.has('KeyD') || this.tasteri.has('ArrowRight')) dx += korak
    if (dx || dz) this.pomeriRavno(dx, dz)
    if (this.tasteri.has('KeyQ')) this.zeljeniYaw += dt * 1.1
    if (this.tasteri.has('KeyE')) this.zeljeniYaw -= dt * 1.1
    if (this.tasteri.has('KeyR')) {
      this.zeljeniPitch = THREE.MathUtils.clamp(this.zeljeniPitch + dt * 0.8, MIN_PITCH, MAX_PITCH)
    }
    if (this.tasteri.has('KeyF')) {
      this.zeljeniPitch = THREE.MathUtils.clamp(this.zeljeniPitch - dt * 0.8, MIN_PITCH, MAX_PITCH)
    }

    this.zeljeniTarget.x = THREE.MathUtils.clamp(this.zeljeniTarget.x, -this.granica, this.granica)
    this.zeljeniTarget.z = THREE.MathUtils.clamp(this.zeljeniTarget.z, -this.granica, this.granica)
    // Cilj klizi po terenu, pa se pogled ne zabija u planinu.
    this.zeljeniTarget.y = Math.max(0, this.hm.sample(this.zeljeniTarget.x, this.zeljeniTarget.z))

    const k = 1 - Math.exp(-dt * 12)
    this.target.lerp(this.zeljeniTarget, k)
    this.yaw += (this.zeljeniYaw - this.yaw) * k
    this.pitch += (this.zeljeniPitch - this.pitch) * k
    this.distanca += (this.zeljenaDistanca - this.distanca) * k

    const horizontalno = Math.cos(this.pitch) * this.distanca
    this.pom.set(
      this.target.x + Math.sin(this.yaw) * horizontalno,
      this.target.y + Math.sin(this.pitch) * this.distanca,
      this.target.z + Math.cos(this.yaw) * horizontalno,
    )

    // Ne dozvoljavamo kameri da propadne kroz teren.
    const podKamerom = Math.max(0, this.hm.sample(this.pom.x, this.pom.z))
    const minVisina = podKamerom + this.hm.maxY * 0.035 + 6
    if (this.pom.y < minVisina) this.pom.y = minVisina

    this.camera.position.copy(this.pom)
    this.camera.lookAt(this.target)
  }
}
